import React,{useCallback,useEffect,useState}from"react";
import{Box,Button,Typography}from"@mui/material";
import{useLocation,useNavigate}from"react-router-dom";
import{useAuth}from"../../context/AuthContext";
import{useAlert}from"../../context/AlertContext";
import NewOrder from"./AdminOrder/NewOrder";
import OrderDetail from"./AdminOrder/OrderDetail";

export default function AdminOrder({administratorId=null}){
  const location=useLocation();
  const navigate=useNavigate();
  const{authFetch,user}=useAuth();
  const{showError}=useAlert();
  const[orders,setOrders]=useState([]);
  const[loading,setLoading]=useState(true);
  const[downloadingId,setDownloadingId]=useState(null);

  const isTechnician=user?.role==="TECHNICIAN";
  const isNewOrder=location.pathname==="/admin-panel/orders/new";
  const orderMatch=location.pathname.match(/^\/admin-panel\/orders\/([a-z0-9-]+)\/([0-9a-fA-F-]{36})$/);
  const contactSlug=orderMatch?orderMatch[1]:null;
  const publicId=orderMatch?orderMatch[2]:null;

  const loadOrders=useCallback(async()=>{
    if(isNewOrder||publicId)return;
    setLoading(true);
    try{
      const url=administratorId?`/api/orden/?administrator=${administratorId}`:"/api/orden/";
      const response=await authFetch(url);
      const data=await response.json();
      if(!response.ok)throw new Error(data.detail||"No se pudieron cargar las órdenes.");
      setOrders(Array.isArray(data)?data:Array.isArray(data.orders)?data.orders:[]);
    }catch(error){
      showError(error.message||"No se pudieron cargar las órdenes.");
    }finally{
      setLoading(false);
    }
  },[authFetch,showError,administratorId,isNewOrder,publicId]);

  useEffect(()=>{loadOrders();},[loadOrders]);

  const openOrder=order=>{
    navigate(`/admin-panel/orders/${order.contact_slug}/${order.public_id}`);
  };

  const downloadOrder=async(order,event)=>{
    event?.stopPropagation();
    setDownloadingId(order.public_id);
    try{
      const response=await authFetch(`/api/orden/${order.contact_slug}/${order.public_id}/pdf/`);
      if(!response.ok){
        let message="No se pudo descargar la orden.";
        try{
          const data=await response.json();
          message=data.detail||message;
        }catch{}
        throw new Error(message);
      }
      const blob=await response.blob();
      const url=window.URL.createObjectURL(blob);
      const link=document.createElement("a");
      link.href=url;
      link.download=`orden_${order.number}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    }catch(error){
      showError(error.message||"No se pudo descargar la orden.");
    }finally{
      setDownloadingId(null);
    }
  };

  const statusColor=status=>{
    if(status==="APROBADA")return"#027A48";
    if(status==="RECHAZADA")return"#B42318";
    if(status==="EN_REVISION")return"#B54708";
    return"#344054";
  };

  const formatDate=value=>{
    if(!value)return"—";
    return new Date(value).toLocaleDateString("es-EC",{day:"2-digit",month:"2-digit",year:"numeric"});
  };

  if(publicId&&contactSlug)return <OrderDetail contactSlug={contactSlug} publicId={publicId}/>;

  if(isNewOrder){
    if(!isTechnician)return(
      <Box sx={{bgcolor:"#fff",border:"1px solid #dce3ea",borderRadius:3,p:4,textAlign:"center"}}>
        <Typography sx={{fontSize:14,color:"#667085"}}>Solo los técnicos pueden generar una nueva orden.</Typography>
      </Box>
    );
    return(
      <NewOrder
        onCreated={order=>{
          navigate(`/admin-panel/orders/${order.contact_slug}/${order.public_id}`);
        }}
      />
    );
  }

  return(
    <Box>
      <Box sx={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:2,mb:2.5}}>
        <Typography sx={{fontSize:24,fontWeight:700,color:"#182230"}}>Órdenes de trabajo</Typography>
        {isTechnician&&(
          <Button
            variant="contained"
            onClick={()=>navigate("/admin-panel/orders/new")}
            sx={{bgcolor:"#123f6d",borderRadius:2,textTransform:"none",fontWeight:700,boxShadow:"none",px:2.2,"&:hover":{bgcolor:"#0e345b",boxShadow:"none"}}}
          >
            Nueva orden
          </Button>
        )}
      </Box>

      <Box sx={{bgcolor:"#fff",border:"1px solid #dce3ea",borderRadius:3,overflowX:"auto"}}>
        <Box sx={{minWidth:1080}}>
          <Box sx={{display:"grid",gridTemplateColumns:"110px 1.4fr 1.4fr 1.4fr 120px 130px 130px",gap:2,px:2.5,py:1.5,bgcolor:"#123f6d",color:"#fff",alignItems:"center"}}>
            <Typography sx={{fontSize:13,fontWeight:700}}>N.º orden</Typography>
            <Typography sx={{fontSize:13,fontWeight:700}}>Equipo</Typography>
            <Typography sx={{fontSize:13,fontWeight:700}}>Técnico</Typography>
            <Typography sx={{fontSize:13,fontWeight:700}}>Contacto</Typography>
            <Typography sx={{fontSize:13,fontWeight:700}}>Fecha</Typography>
            <Typography sx={{fontSize:13,fontWeight:700}}>Estado</Typography>
            <Typography sx={{fontSize:13,fontWeight:700,textAlign:"right"}}>Documento</Typography>
          </Box>

          {loading&&(
            <Box sx={{py:6,textAlign:"center"}}>
              <Typography sx={{fontSize:14,color:"#667085"}}>Cargando órdenes...</Typography>
            </Box>
          )}

          {!loading&&orders.length===0&&(
            <Box sx={{py:6,textAlign:"center"}}>
              <Typography sx={{fontSize:14,color:"#667085"}}>No hay órdenes de trabajo registradas.</Typography>
            </Box>
          )}

          {!loading&&orders.map(order=>(
            <Box
              key={order.public_id}
              onClick={()=>openOrder(order)}
              sx={{
                display:"grid",
                gridTemplateColumns:"110px 1.4fr 1.4fr 1.4fr 120px 130px 130px",
                gap:2,
                px:2.5,
                py:1.5,
                alignItems:"center",
                borderBottom:"1px solid #edf1f5",
                cursor:"pointer",
                "&:last-child":{borderBottom:"none"},
                "&:hover":{bgcolor:"#f8fafc"},
              }}
            >
              <Typography sx={{fontSize:13.5,fontWeight:700,color:"#182230"}}>{order.number}</Typography>

              <Box sx={{minWidth:0}}>
                <Typography sx={{fontSize:13.5,fontWeight:600,color:"#344054",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                  {order.equipment||"—"}
                </Typography>
                {(order.brand||order.equipment_model)&&(
                  <Typography sx={{fontSize:11.5,color:"#98A2B3"}}>
                    {order.brand||""}{order.brand&&order.equipment_model?" · ":""}{order.equipment_model||""}
                  </Typography>
                )}
              </Box>

              <Typography translate="no" className="notranslate" sx={{fontSize:13.5,color:"#344054"}}>
                {order.technician?.name||"—"}
              </Typography>

              <Typography translate="no" className="notranslate" sx={{fontSize:13.5,color:"#344054"}}>
                {order.supervisor?.name||"—"}
              </Typography>

              <Typography sx={{fontSize:13,color:"#667085"}}>{formatDate(order.created_at)}</Typography>

              <Typography sx={{fontSize:12.5,fontWeight:700,color:statusColor(order.status)}}>
                {order.status_display||order.status}
              </Typography>

              <Box sx={{display:"flex",justifyContent:"flex-end"}}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={downloadingId===order.public_id}
                  onClick={event=>downloadOrder(order,event)}
                  sx={{borderRadius:2,textTransform:"none",fontWeight:700,fontSize:12,color:"#123f6d",borderColor:"#b8c8d8",boxShadow:"none","&:hover":{bgcolor:"#f5f8fb",borderColor:"#123f6d",boxShadow:"none"}}}
                >
                  {downloadingId===order.public_id?"Descargando...":"Descargar"}
                </Button>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}